import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateUserSettings, fetchCurrentUser, fetchUserSettings, fetchSounds } from '../services/apiService';
import './UserConfigScreen.css';

const UserConfigScreen = ({ user, onClose, onUserUpdate, globalMuted, globalVolume }) => {
  const { t } = useTranslation();
  const [excludeFromStats, setExcludeFromStats] = useState(false);
  const [streamerMode, setStreamerMode] = useState(false);
  const [soundAlertsEnabled, setSoundAlertsEnabled] = useState(true);
  const [selectedTimerAlert, setSelectedTimerAlert] = useState(null);
  const [noAds, setNoAds] = useState(false);
  const [sounds, setSounds] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(true);
  const [isSoundsLoading, setIsSoundsLoading] = useState(true);

  // Fetch user settings when component mounts
  useEffect(() => {
    const getUserSettings = async () => {
      try {
        setIsSettingsLoading(true);
        const settings = await fetchUserSettings();
        setExcludeFromStats(settings.exclude_from_stats || false);
        setStreamerMode(settings.streamer_mode || false);
        setSoundAlertsEnabled(settings.sound_alerts_enabled !== false); // Default to true if not explicitly false
        setSelectedTimerAlert(settings.selected_timer_alert || null);
        setNoAds(settings.no_ads || false);
      } catch (error) {
        console.error('Error fetching user settings:', error);
        // Fallback to user data if available
        if (user) {
          setExcludeFromStats(user.exclude_from_stats || false);
          setStreamerMode(user.streamer_mode || false);
          setSoundAlertsEnabled(user.sound_alerts_enabled !== false); // Default to true if not explicitly false
          setSelectedTimerAlert(user.selected_timer_alert || null);
          setNoAds(user.no_ads || false);
        }
      } finally {
        setIsSettingsLoading(false);
      }
    };

    getUserSettings();
  }, [user]);

  // Fetch available sounds when component mounts
  useEffect(() => {
    const getSounds = async () => {
      try {
        setIsSoundsLoading(true);
        const soundsData = await fetchSounds();
        setSounds(soundsData);
      } catch (error) {
        console.error('Error fetching sounds:', error);
      } finally {
        setIsSoundsLoading(false);
      }
    };

    getSounds();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setNotification(null);

    try {
      // Make API call to update the user's settings
      const settings = {
        exclude_from_stats: excludeFromStats,
        streamer_mode: streamerMode,
        sound_alerts_enabled: soundAlertsEnabled,
        selected_timer_alert: selectedTimerAlert,
        no_ads: noAds
      };

      await updateUserSettings(settings);

      // Fetch the updated user data
      if (onUserUpdate) {
        try {
          const updatedUser = await fetchCurrentUser();
          if (updatedUser && !updatedUser.error) {
            onUserUpdate(updatedUser);
          }
        } catch (fetchError) {
          console.error('Error fetching updated user data:', fetchError);
        }
      }

      setNotification({
        type: 'success',
        message: t('userConfig.saveSuccess')
      });

      // No longer automatically closing the screen after saving
    } catch (error) {
      console.error('Error saving user configuration:', error);
      setNotification({
        type: 'error',
        message: t('userConfig.saveError')
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-config-screen">
      <div className="user-config-container">
        <div className="user-config-header">
          <h1>
            <i className="fas fa-cog"></i> {t('userConfig.title')}
          </h1>
        </div>

        {notification && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        {isSettingsLoading ? (
          <div className="loading-container">
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="excludeFromStats"
                  checked={excludeFromStats}
                  onChange={(e) => setExcludeFromStats(e.target.checked)}
                />
                <label htmlFor="excludeFromStats">{t('userConfig.excludeFromStats')}</label>
              </div>
              <p className="form-help">{t('userConfig.excludeFromStatsHelp')}</p>
            </div>

            <div className="form-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="streamerMode"
                  checked={streamerMode}
                  onChange={(e) => setStreamerMode(e.target.checked)}
                />
                <label htmlFor="streamerMode">{t('userConfig.streamerMode')}</label>
              </div>
              <p className="form-help">{t('userConfig.streamerModeHelp')}</p>
            </div>

            <div className="form-group">
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  id="soundAlertsEnabled"
                  checked={soundAlertsEnabled}
                  onChange={(e) => setSoundAlertsEnabled(e.target.checked)}
                />
                <label htmlFor="soundAlertsEnabled">{t('userConfig.soundAlertsEnabled', 'Enable Sound Alerts')}</label>
              </div>
              <p className="form-help">{t('userConfig.soundAlertsEnabledHelp', 'Enable all sound alerts in the game')}</p>
            </div>


            <div className="form-group">
              <label htmlFor="selectedTimerAlert">{t('userConfig.selectedTimerAlert', 'Timer Alert Sound')}</label>
              <select
                id="selectedTimerAlert"
                value={selectedTimerAlert || ''}
                onChange={(e) => setSelectedTimerAlert(e.target.value === '' ? null : e.target.value)}
                disabled={isSoundsLoading}
              >
                <option value="">{t('userConfig.defaultAlert', 'Default Alert')}</option>
                {sounds.map(sound => (
                  <option key={sound.code} value={sound.code}>
                    {sound.name}
                  </option>
                ))}
              </select>
              <p className="form-help">{t('userConfig.selectedTimerAlertHelp', 'Select sound to play when timer reaches 10 seconds')}</p>

              {selectedTimerAlert && (
                <div className="music-preview">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const sound = sounds.find(s => s.code === selectedTimerAlert);
                      if (sound && !globalMuted) {
                        const audio = new Audio(sound.path);
                        audio.volume = globalVolume / 100; // Set volume based on globalVolume (0-100 to 0-1)
                        audio.play();
                      }
                    }}
                  >
                    <i className="fas fa-play"></i> {t('userConfig.previewSound', 'Preview')}
                  </button>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
              >
                <i className="fas fa-home"></i> {t('common.back')}
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={isLoading}
              >
                <i className="fas fa-save"></i> {isLoading ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserConfigScreen;
